import React from 'react';
import { DropdownSelect, type CodeDefinitionOption } from './dropdown-select';
import { InlineEdit } from './inline-edit';

type InlineDropdownEditProps = {
  value: CodeDefinitionOption | { value: string; label: string } | string | null;
  onChange: (value: CodeDefinitionOption | null) => void;
  options: CodeDefinitionOption[] | undefined;
  placeholder?: string;
  displayValue?: (
    value: CodeDefinitionOption | { value: string; label: string } | string | null
  ) => string;
  small?: boolean;
  editOn?: 'click' | 'hover' | 'focus';
  disabled?: boolean;
  isClearable?: boolean;
  className?: string;
};

export function InlineDropdownEdit({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  displayValue,
  small = false,
  editOn = 'hover',
  disabled = false,
  isClearable = false,
  className,
}: InlineDropdownEditProps) {
  const handleDisplay = (
    val: CodeDefinitionOption | { value: string; label: string } | string | null
  ) => {
    if (displayValue) {
      return displayValue(val);
    }

    if (!val) {
      return placeholder;
    }

    if (typeof val === 'string') {
      // If value is a string, try to find the option
      const option = options?.find((opt) => opt.value === val);
      return option?.label || val;
    }

    // If value is an object with label property
    if ('label' in val && val.label) {
      return val.label;
    }

    return placeholder;
  };

  // Convert value to CodeDefinitionOption format for DropdownSelect
  const selectValue = React.useMemo(() => {
    if (!value) return null;

    if (typeof value === 'string') {
      const option = options?.find((opt) => opt.value === value);
      return option || null;
    }

    if ('value' in value && 'label' in value) {
      return value as CodeDefinitionOption;
    }

    return null;
  }, [value, options]);

  return (
    <InlineEdit<CodeDefinitionOption | null>
      value={selectValue}
      onChange={onChange}
      renderDisplay={handleDisplay}
      renderEditor={(val, handleChange, onBlur, onFocus) => (
        <DropdownSelect
          allOptions={options}
          value={val}
          isMulti={false}
          onChange={(selectedOption) => {
            // InlineDropdownEdit only supports single select
            // DropdownSelect onChange can return array for multi-select, but we're using isMulti={false}
            const singleValue = Array.isArray(selectedOption)
              ? selectedOption.length > 0
                ? selectedOption[0]
                : null
              : selectedOption;
            handleChange(singleValue as CodeDefinitionOption | null);
            // Close the inline edit after selection
            setTimeout(() => {
              onBlur();
            }, 100);
          }}
          placeholder={placeholder}
          isDisabled={disabled}
          menuPortalTarget={document.body}
          onFocus={onFocus}
          small={small}
          isClearable={isClearable}
        />
      )}
      editOn={editOn}
      disabled={disabled}
      className={className}
    />
  );
}
